/**
 * "Похожее из памяти" on a task (design spec §8, addendum §2).
 *
 * Collapsible and closed by default: precedents are context you reach for, not
 * something that should push the task's own content down the screen.
 *
 * Two independent flags ride on each card and are rendered as two separate
 * badges (FEAT-025): `has_error_flag` → "N ошибок" in `--dstr`, meaning the
 * approach was wrong; `has_superseded_flag` → "устарело" in `--sep`/`--hint`,
 * meaning it was right and the ground moved. Collapsing them into one warning
 * would tell the reader a stale precedent was a mistake, which is the exact
 * misreading FEAT-025 exists to prevent.
 */

import { useQuery } from "@tanstack/react-query";
import { Brain } from "lucide-react";

import { Search } from "@/api/resources";
import type { SimilarityCard } from "@/api/types";
import { Badge, ErrorCountBadge, SupersededBadge, ToolSlugChip } from "@/components/ui/Badge";
import { ClickableCard } from "@/components/ui/Card";
import { CollapsibleSection } from "@/components/ui/Section";
import { SkeletonBlock } from "@/components/ui/Skeleton";
import { LinkSimilar } from "@/components/TaskLinks";
import { formatMonthYearPrepositional, parseDate } from "@/lib/format";

export interface TaskMemoryProps {
  /** Current task, excluded from its own results. */
  taskId: string;
  /** What to search for — the task's summary plus title. */
  query: string;
}

export function TaskMemory({ taskId, query }: TaskMemoryProps) {
  const q = query.trim();
  const memory = useQuery({
    queryKey: ["memory-similar", q],
    queryFn: () => Search.similarTasks(q, { scope: "all", limit: 6 }),
    enabled: q.length > 0,
    staleTime: 5 * 60_000,
  });

  // A task always matches itself, and offering it as its own precedent is
  // noise — drop it before deciding whether there is anything to show.
  const cards = (memory.data ?? []).filter((c) => c.task_id !== taskId);

  if (memory.isLoading) {
    return <SkeletonBlock className="h-11" />;
  }
  // Nothing found is not a state worth a section header, let alone an empty
  // state: the absence of a precedent is simply silence.
  if (memory.isError || cards.length === 0) return null;

  return (
    <CollapsibleSection
      title="Похожее из памяти"
      count={cards.length}
      icon={<Brain className="h-5 w-5 shrink-0 text-hint" aria-hidden="true" />}
    >
      {cards.map((card) => (
        <div key={card.task_id} className="flex flex-col gap-1">
          <MemoryCard card={card} />
          {/* Spec 017: outside the card, which is itself a link. */}
          <LinkSimilar
            taskId={taskId}
            otherId={card.task_id}
            title={card.task_summary ?? "Похожая задача"}
          />
        </div>
      ))}
    </CollapsibleSection>
  );
}

function MemoryCard({ card }: { card: SimilarityCard }) {
  const appliedAt = parseDate(card.applied_at);
  return (
    <ClickableCard to={`/tasks/${card.task_id}`} ariaLabel={card.task_summary ?? "Похожая задача"}>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-start justify-between gap-2">
          <p className="clamp-2 text-cell-title font-medium text-text">
            {card.task_summary ?? "Без описания"}
          </p>
          <div className="flex shrink-0 flex-col items-end gap-1">
            {card.has_error_flag && <ErrorCountBadge count={1} />}
            {card.has_superseded_flag && <SupersededBadge />}
          </div>
        </div>

        {card.approach_gist && (
          <p className="clamp-2 text-body text-hint">{card.approach_gist}</p>
        )}

        {card.tools_used.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {card.tools_used.map((slug) => (
              <ToolSlugChip key={slug} slug={slug} />
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 text-chip text-hint">
          {card.status === "active" ? (
            <Badge tone="accent">в работе</Badge>
          ) : (
            appliedAt && <span>применяли {formatMonthYearPrepositional(appliedAt)}</span>
          )}
        </div>
      </div>
    </ClickableCard>
  );
}
